import { useId, useMemo } from 'react'
import { useParentSize } from '@visx/responsive'
import type { ChartResult, ChartSpec } from '../../../types/chart.js'
import { useNls } from '../../../nls/nlsProvider.js'
import type { NlsKey } from '../../../nls/strings_nls.js'
import { getSeriesStyle } from './chartTheme.js'
import { BarChart } from './BarChart.js'
import { LineChart } from './LineChart.js'
import { AreaChart } from './AreaChart.js'
import { DonutChart } from './DonutChart.js'
import { NumberChart } from './NumberChart.js'
import { TableChart } from './TableChart.js'
import { GaugeChart } from './GaugeChart.js'
import { ProgressChartRenderer } from './ProgressChartRenderer.js'
import { BurnChart } from './BurnChart.js'
import { TargetActualChart } from './TargetActualChart.js'
import { ComboChart } from './ComboChart.js'
import './chart.css'

export interface ChartProps {
  spec: ChartSpec
  data: ChartResult
  width?: number
  height?: number
  className?: string
  testId?: string
  compact?: boolean
}

function formatSeriesLabel(label: string, key: string | undefined, t: (k: NlsKey) => string): string {
  if (key === '__other' || label === '__other') {
    return t('NLSChartSeriesOther')
  }
  if (key === '__unassigned' || label === '__unassigned') {
    return t('NLSChartSeriesUnassigned')
  }
  return label
}

export function Chart({
  spec,
  data,
  width,
  height,
  className = '',
  testId,
  compact = false,
}: ChartProps) {
  const { parentRef, width: measuredWidth, height: measuredHeight } = useParentSize({
    debounceTime: 10,
    ignoreDimensions: 'height',
  })
  const { t } = useNls()
  const figId = useId()
  const descId = `${figId}-desc`
  const tableId = `${figId}-table`

  // Normalize labels: map __other and __unassigned to localized strings
  const normalizedData: ChartResult = useMemo(() => {
    return {
      ...data,
      series: data.series.map((s) => ({
        ...s,
        label: formatSeriesLabel(s.label, s.key, t),
        points: s.points.map((p) => {
          const xStr = String(p.x)
          const normX =
            xStr === '__other'
              ? t('NLSChartSeriesOther')
              : xStr === '__unassigned'
                ? t('NLSChartSeriesUnassigned')
                : p.x
          return {
            ...p,
            x: normX,
          }
        }),
      })),
    }
  }, [data, t])

  // Collect all distinct X values for the hidden screen-reader table
  const allXKeys = useMemo(() => {
    const set = new Set<string>()
    normalizedData.series.forEach((s) => {
      s.points.forEach((p) => set.add(String(p.x)))
    })
    return Array.from(set)
  }, [normalizedData.series])

  const showLegend = !compact && spec.style?.legend !== 'off' && normalizedData.series.length > 0 && spec.type !== 'number' && spec.type !== 'table'

  const renderPlot = (plotWidth: number, plotHeight: number) => {
    const effectiveWidth = Math.max(plotWidth || (compact ? 120 : 240), 20)
    const effectiveHeight = Math.max(plotHeight || (compact ? 40 : 220), 20)

    switch (spec.type) {
      case 'bar':
        return <BarChart spec={spec} data={normalizedData} width={effectiveWidth} height={effectiveHeight} compact={compact} />
      case 'line':
        return <LineChart spec={spec} data={normalizedData} width={effectiveWidth} height={effectiveHeight} compact={compact} />
      case 'area':
        return <AreaChart spec={spec} data={normalizedData} width={effectiveWidth} height={effectiveHeight} />
      case 'donut':
        return <DonutChart spec={spec} data={normalizedData} width={effectiveWidth} height={effectiveHeight} />
      case 'number':
        return <NumberChart spec={spec} data={normalizedData} />
      case 'table':
        return <TableChart spec={spec} data={normalizedData} />
      case 'gauge':
        return <GaugeChart spec={spec} data={normalizedData} width={effectiveWidth} height={effectiveHeight} />
      case 'progress':
        return <ProgressChartRenderer spec={spec} data={normalizedData} />
      case 'burn':
        return <BurnChart spec={spec} data={normalizedData} width={effectiveWidth} height={effectiveHeight} />
      case 'targetActual':
        return <TargetActualChart spec={spec} data={normalizedData} width={effectiveWidth} height={effectiveHeight} />
      case 'combo':
        return <ComboChart spec={spec} data={normalizedData} width={effectiveWidth} height={effectiveHeight} />
      default:
        return <BarChart spec={spec} data={normalizedData} width={effectiveWidth} height={effectiveHeight} compact={compact} />
    }
  }

  return (
    <figure
      className={`chartRoot ${compact ? 'isCompact ' : ''}${className}`.trim()}
      data-testid={testId || 'chart-root'}
      data-compact={compact ? 'true' : undefined}
      aria-describedby={descId}
    >
      {!compact && (spec.title || spec.subtitle) && (
        <div className="chartHeader">
          {spec.title && <h3 className="chartTitle">{spec.title}</h3>}
          {spec.subtitle && <p className="chartSubtitle">{spec.subtitle}</p>}
        </div>
      )}

      {/* Accessible description for screen readers */}
      <figcaption id={descId} className="sr-only">
        {spec.title ? `${spec.title}. ` : ''}
        {spec.subtitle ? `${spec.subtitle}. ` : ''}
        {`Chart type: ${spec.type}. `}
        {normalizedData.asOf ? `Data as of ${normalizedData.asOf}. ` : ''}
        {`Full data table follows in table id ${tableId}.`}
      </figcaption>

      {/* Hidden screen-reader data table containing EVERY point */}
      <table
        id={tableId}
        className="sr-only"
        aria-label={t('NLSChartA11yDataTable')}
      >
        <caption>{spec.title || 'Chart Data'}</caption>
        <thead>
          <tr>
            <th scope="col">Dimension</th>
            {normalizedData.series.map((s, idx) => (
              <th key={s.key || idx} scope="col">
                {s.label || `Series ${idx + 1}`}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {allXKeys.map((xKey) => (
            <tr key={xKey}>
              <td>{xKey}</td>
              {normalizedData.series.map((s, idx) => {
                const point = s.points.find((p) => String(p.x) === xKey)
                return (
                  <td key={s.key || idx}>
                    {point !== undefined ? point.y : '—'}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Responsive visual chart container */}
      <div className="chartWrapper">
        {width && height ? (
          renderPlot(width, height)
        ) : (
          <div ref={parentRef} style={{ width: '100%' }}>
            {renderPlot(
              width ?? (measuredWidth > 0 ? measuredWidth : 0),
              measuredHeight > 0 ? measuredHeight : (height ?? 240),
            )}
          </div>
        )}
      </div>

      {/* Optional Legend */}
      {showLegend && (
        <div className="chartLegend" role="list" aria-label="Legend">
          {normalizedData.series.map((series, idx) => {
            const style = getSeriesStyle(idx, spec.style?.seriesColor?.[series.key])
            return (
              <div key={series.key || idx} className="chartLegendItem" role="listitem">
                <span
                  className="chartLegendSwatch"
                  style={{
                    backgroundColor: style.fill,
                    border: style.strokeDasharray ? `1px dashed ${style.color}` : undefined,
                  }}
                  aria-hidden="true"
                />
                <span>{series.label}</span>
              </div>
            )
          })}
        </div>
      )}
    </figure>
  )
}
